import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';

import { EkycListType } from './ekyc-request.enum';
import { IEkycRequestFilter, IRiskOverridePayload, IStatusUpdatePayload } from './ekyc-request.model';

/**
 * Assisted eKYC request API client.
 *
 * Endpoint shape mirrors the existing bank-account-opening module in this repo
 * (`account/admin/account-opening` with `requests`, `maker/requests`, `checker/requests`),
 * extended with the COGB tier the SRS requires.
 */
@Injectable({ providedIn: 'root' })
export class EkycRequestService {
  private readonly resourceUrl: string;

  constructor(
    protected readonly http: HttpClient,
    protected readonly commonService: CommonService,
    applicationConfigService: ApplicationConfigService,
  ) {
    this.resourceUrl = applicationConfigService.getEndpointFor('account/admin/assisted-ekyc');
  }

  private segmentFor(listType: EkycListType): string {
    return listType === 'all' ? 'requests' : `${listType}/requests`;
  }

  fetchRequestList(listType: EkycListType, query: IEkycRequestFilter): Observable<any> {
    return this.http.get<any>(`${this.resourceUrl}/${this.segmentFor(listType)}?${this.commonService.toQueryString(query)}`);
  }

  requestDetails(requestId: string): Observable<any> {
    return this.http.get<any>(`${this.resourceUrl}/requests/${requestId}`);
  }

  updateStatus(listType: EkycListType, payload: IStatusUpdatePayload): Observable<any> {
    const action = listType === 'cogb' ? 'cogb-action' : 'checker-action';
    return this.http.post<any>(`${this.resourceUrl}/${action}`, payload);
  }

  /** Creates or updates a Draft from the wizard form. */
  saveDraft(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/drafts`, payload);
  }

  /** Submits the wizard form — screening and grading run server-side before BRANCH_REVIEW. */
  submitRequest(payload: unknown): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/requests`, payload);
  }

  overrideRisk(payload: IRiskOverridePayload): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/risk-override`, payload);
  }

  getBranchList(): Observable<any> {
    return this.http.get<any>('api/v1/meta/account-opening/own-bank-branches');
  }

  export(query: IEkycRequestFilter): Observable<any> {
    const params = { ...query } as any;
    delete params.pageNumber;
    delete params.pageSize;
    return this.http.get(`${this.resourceUrl}/requests-export?exportType=xls&${this.commonService.toQueryString(params)}`, {
      responseType: 'blob',
    });
  }
}
